import {
  Button,
  Card,
  Checkbox,
  DropdownMenu,
  Input,
  Progress,
  Spinner,
  DataTable,
  confirmDialog,
  Alert,
  Badge,
  PlayerCell,
} from "@/components/ui";
import { PaginationParams, SuperPage, SuperPageAssignment } from "@/services/bedrock";
import type { GetSuperPageAssignmentsParams } from "@/services/bedrock/SuperPageServices";
import { SuperPageServices } from "@/services/bedrock/SuperPageServices";
import { useAccountSettings, useDebounced, useSuperPageAssignmentSearch } from "@/utils";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import {
  Eye,
  BellRing,
  MoreHorizontal,
  MousePointerClick,
  AlertTriangleIcon,
  Plus,
  Pencil,
  SquareArrowOutUpRight,
  ListFilter,
  CircleCheck,
  Circle,
  Link,
  X,
} from "lucide-react";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import pluralize from "pluralize";
import { WebComponents } from "@/interfaces";
import { SuperPageEligibilityDialog } from "./SuperPageEligibilityDialog";
import { SuperPageAssignmentStatus, timestampLabel } from "./Assignments/SuperPageAssignmentStatus";
import { createSortableHeader } from "@/components/RecordSelectionDialog";
import { VideoPageModal } from "../VideoPages";

const assignmentStatusSortableHeader = createSortableHeader("Assignment Status");

type Props = {
  superPage: SuperPage;
  onGoToPageDetails: () => void;
  onPreviewAssignment: (assignment: SuperPageAssignment) => void;
};

const SuperPagePlayers: React.FC<Props> = ({ superPage, onGoToPageDetails, onPreviewAssignment }) => {
  const [editingAssignment, setEditingAssignment] = useState<SuperPageAssignment>();
  const [pagination, setPagination] = useState<PaginationParams>({ page: 1, perPage: 25 });
  const [showEligibility, setShowEligibility] = useState(false);
  const [selectedAssignments, setSelectedAssignments] = useState<Map<number, SuperPageAssignment>>(new Map());
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.VIDEO_PAGES);

  const [assignmentStatus, setAssignmentStatus] = useState<SuperPageAssignment.Status>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search.trim(), 350);
  const prevDebouncedSearch = useRef(debouncedSearch);

  const superPageIsActive = useMemo(() => superPage.status === "ACTIVE", [superPage.status]);

  const listParams = useMemo((): GetSuperPageAssignmentsParams => {
    const sortEntry = sorting[0];
    return {
      superPageId: superPage.id,
      search: debouncedSearch || undefined,
      ...pagination,
      status: assignmentStatus,
      extraFields: ["player", "totalViews", "totalClicks", "contentPage"],
      ...(sortEntry && {
        sort: sortEntry.id as NonNullable<GetSuperPageAssignmentsParams["sort"]>,
        order: sortEntry.desc ? "desc" : "asc",
      }),
    };
  }, [superPage.id, debouncedSearch, pagination, assignmentStatus, sorting]);

  const { superPageAssignments, isFetching, clearSuperPageAssignmentsSearchCache } =
    useSuperPageAssignmentSearch(listParams);

  const assignments = superPageAssignments?.content ?? [];
  const totalPages = superPageAssignments?.pagination?.totalPages ?? 0;
  const totalElements = superPageAssignments?.pagination?.totalCount ?? 0;
  const hasFiltersApplied = Boolean(debouncedSearch) || assignmentStatus;

  useEffect(() => {
    if (prevDebouncedSearch.current !== debouncedSearch) {
      prevDebouncedSearch.current = debouncedSearch;
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [debouncedSearch]);

  const sortingKeyRef = useRef("");
  useEffect(() => {
    const key = sorting[0] ? `${sorting[0].id}:${sorting[0].desc}` : "";
    if (sortingKeyRef.current !== key) {
      sortingKeyRef.current = key;
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [sorting]);

  const handleSortingChange = useCallback((updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    setSorting(prev => (typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue));
  }, []);

  const handlePaginationChange = useCallback(
    (updaterOrValue: PaginationParams | ((old: PaginationParams) => PaginationParams)) => {
      setPagination(prev => (typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue));
    },
    []
  );

  const handleRequestAssignments = async (assignmentsToRequest: SuperPageAssignment[]) => {
    if (!superPage || assignmentsToRequest.length === 0) return;

    setShowEligibility(false);
    const total = assignmentsToRequest.length;
    const toastId = "request-players-progress";
    toast.loading(`Requesting ${pluralize("Player", total, true)}`, { id: toastId });
    try {
      const response = await SuperPageServices.updateSuperPageAssignmentsBulk(
        superPage.id,
        assignmentsToRequest.map(a => a.id),
        "REQUESTED"
      );
      toast.dismiss(toastId);
      if (response.successes.length > 0) {
        toast.success(
          `${pluralize("Player", response.successes.length, true)} requested to customize their Video Page`
        );
      }
      const failedIds = Object.keys(response.errors);
      if (failedIds.length > 0) {
        const failedPlayers = failedIds.map(
          id => assignmentsToRequest.find(a => a.id === parseInt(id))?.player?.fullName ?? "Unknown Player"
        );
        toast.error(
          `Failed to request ${pluralize("Player", failedPlayers.length, true)}: ${failedPlayers.join(", ")}`
        );
        console.error(response.errors);
      }
    } catch (error) {
      console.error(error);
      toast.error(`Failed to request Players`);
    } finally {
      clearSuperPageAssignmentsSearchCache();
    }
  };

  const visibleAssignmentIds = useMemo(() => new Set(assignments.map(a => a.id)), [assignments]);
  const allVisibleSelected = assignments.length > 0 && assignments.every(a => selectedAssignments.has(a.id));
  const someVisibleSelected = assignments.some(a => selectedAssignments.has(a.id));

  const toggleRowSelection = useCallback((assignment: SuperPageAssignment) => {
    setSelectedAssignments(prev => {
      const next = new Map(prev);
      if (next.has(assignment.id)) next.delete(assignment.id);
      else next.set(assignment.id, assignment);
      return next;
    });
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    if (allVisibleSelected) {
      setSelectedAssignments(prev => {
        const next = new Map(prev);
        visibleAssignmentIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedAssignments(prev => {
        const next = new Map(prev);
        assignments.forEach(a => next.set(a.id, a));
        return next;
      });
    }
  }, [allVisibleSelected, visibleAssignmentIds, assignments]);

  const confirmCancelRequestAssignments = async (assignmentsToCancel: SuperPageAssignment[]) => {
    const requestedAssignments = assignmentsToCancel.filter(a => a.status === "REQUESTED");
    if (requestedAssignments.length === 0) return null;

    var description: React.ReactNode;
    if (requestedAssignments.length === 1) {
      description = (
        <div>
          Are you sure you want to cancel the request for{" "}
          <span className="font-semibold">{requestedAssignments[0].player?.fullName ?? "Player"}</span>? They will no
          longer be asked to customize their Video Page.
        </div>
      );
    } else {
      description = (
        <div>
          Are you sure you want to cancel the request for the selected{" "}
          <span className="font-semibold">{requestedAssignments.length} Players</span>? They will no longer be asked to
          customize their video pages.
        </div>
      );
    }

    const response = await confirmDialog({
      title: `Cancel ${pluralize("Request", requestedAssignments.length)}`,
      description,
      confirmLabel: "Cancel Request",
      cancelLabel: "Keep Request",
    });
    return response ? requestedAssignments : null;
  };

  const confirmRemindAssignments = async (assignmentsToRemind: SuperPageAssignment[]) => {
    if (assignmentsToRemind.length === 0) return null;

    var description: React.ReactNode;
    if (assignmentsToRemind.length === 1) {
      description = (
        <div>
          Are you sure you want to send a reminder to{" "}
          <span className="font-semibold">{assignmentsToRemind[0].player?.fullName ?? "Player"}</span>?
        </div>
      );
    } else {
      description = (
        <div>
          Are you sure you want to send reminders to the selected{" "}
          <span className="font-semibold">{assignmentsToRemind.length} Players</span>?
        </div>
      );
    }

    const response = await confirmDialog({
      title: `Send ${pluralize("Reminder", assignmentsToRemind.length)}`,
      description,
    });
    return response ? assignmentsToRemind : null;
  };

  const cancelRequestAssignments = async (superPage: SuperPage, assignmentsToCancel: SuperPageAssignment[]) => {
    const confirmedAssignments = await confirmCancelRequestAssignments(assignmentsToCancel);
    if (!confirmedAssignments) return;

    const toastId = "cancel-request-players-progress";
    toast.loading(`Canceling ${pluralize("request", confirmedAssignments.length, true)}`, { id: toastId });
    try {
      const response = await SuperPageServices.updateSuperPageAssignmentsBulk(
        superPage.id,
        confirmedAssignments.map(a => a.id),
        "AVAILABLE"
      );
      toast.dismiss(toastId);
      if (response.successes.length > 0) {
        toast.success(`${pluralize("Request", response.successes.length, true)} canceled`);
      }
      const failedIds = Object.keys(response.errors);
      if (failedIds.length > 0) {
        const failedAssignments = failedIds.map(
          id => confirmedAssignments.find(spa => spa.id === parseInt(id))?.player?.fullName ?? "Unknown Player"
        );
        toast.error(
          `Failed to cancel ${pluralize("request", failedAssignments.length, true)}: ${failedAssignments.join(", ")}`
        );
        console.error(response.errors);
      }
    } catch (error) {
      console.error(error);
      toast.error(`Failed to cancel requests`);
    } finally {
      setSelectedAssignments(new Map());
      clearSuperPageAssignmentsSearchCache();
    }
  };

  const remindAssignments = async (superPage: SuperPage, assignmentsToRemind: SuperPageAssignment[]) => {
    const confirmedAssignments = await confirmRemindAssignments(assignmentsToRemind);
    if (!confirmedAssignments) return;

    const toastId = "remind-assignments-progress";
    toast.loading(`Sending reminders to ${pluralize("Player", confirmedAssignments.length, true)}`, { id: toastId });
    try {
      const response = await SuperPageServices.remindSuperPageAssignmentsBulk(
        superPage.id,
        confirmedAssignments.map(a => a.id)
      );
      toast.dismiss(toastId);
      if (response.successes.length > 0) {
        toast.success(`${pluralize("Player", response.successes.length, true)} reminders sent`);
      }
      const failedIds = Object.keys(response.errors);
      if (failedIds.length > 0) {
        const failedAssignments = failedIds.map(
          id => confirmedAssignments.find(spa => spa.id === parseInt(id))?.player?.fullName ?? "Unknown Player"
        );
        toast.error(
          `Failed to send reminders to ${pluralize("Player", failedAssignments.length, true)}: ${failedAssignments.join(
            ", "
          )}`
        );
        console.error(response.errors);
      }
    } catch (error) {
      console.error(error);
      toast.error(`Failed to send reminders`);
    } finally {
      clearSuperPageAssignmentsSearchCache();
    }
  };

  const handleCopyLink = useCallback(
    (assignment: SuperPageAssignment) => {
      if (!superPage) return;
      if (assignment.contentPage) {
        navigator.clipboard.writeText(assignment.contentPage.url);
      } else if (superPage.url) {
        const url = new URL(superPage.url);
        if (assignment.player?.email) {
          url.searchParams.set("p", assignment.player.email);
        }
        navigator.clipboard.writeText(url.toString());
      } else {
        toast.error(`No link available to copy`);
        return;
      }
      toast.success(`Link copied to clipboard`);
    },
    [superPage?.url]
  );

  const handleCancelRequestAssignment = useCallback(
    async (assignment: SuperPageAssignment) => {
      if (!superPage) return;
      cancelRequestAssignments(superPage, [assignment]);
    },
    [superPage.id, cancelRequestAssignments]
  );

  const handleRemindAssignment = useCallback(
    async (assignment: SuperPageAssignment) => {
      if (!superPage) return;
      remindAssignments(superPage, [assignment]);
    },
    [superPage.id, remindAssignments]
  );

  const handleOpenInNewTab = useCallback(
    (assignment: SuperPageAssignment) => {
      if (!superPage) return;
      window.open(assignment.url, "_blank");
    },
    [superPage.id]
  );

  const clearSelections = useCallback(() => {
    setSelectedAssignments(new Map());
  }, []);

  const { totalCompleted, totalRequested } = superPage.assignmentsOverview ?? { totalCompleted: 0, totalRequested: 0 };
  const totalActionable = totalRequested + totalCompleted;
  const completePercentage = totalActionable === 0 ? 0 : Math.round((totalCompleted / totalActionable) * 100);

  const columns: ColumnDef<SuperPageAssignment>[] = [
    {
      id: "select",
      header: () => (
        <Checkbox
          checked={allVisibleSelected}
          indeterminate={someVisibleSelected && !allVisibleSelected}
          onCheckedChange={toggleSelectAllVisible}
          aria-label="Select all visible Players"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedAssignments.has(row.original.id)}
          onCheckedChange={() => toggleRowSelection(row.original)}
          aria-label={`Select ${row.original.player?.fullName ?? "Player"}`}
        />
      ),
      meta: { intrinsic: true },
    } as ColumnDef<SuperPageAssignment>,
    {
      id: "first_name",
      accessorFn: row => row.player?.firstName ?? "",
      header: context => {
        return (
          <div className="flex items-center gap-1">
            {createSortableHeader("Player")(context)}
            {selectedAssignments.size > 0 && (
              <Badge className="flex items-center gap-0.5 pr-1.5 pl-3 hover:bg-primary">
                {selectedAssignments.size} Selected
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={clearSelections}
                  className="hover:bg-white/25 hover:text-white rounded-full"
                >
                  <X className="size-3" strokeWidth={3} />
                </Button>
              </Badge>
            )}
          </div>
        );
      },
      cell: ({ row }) => {
        const player = row.original.player;
        if (!player) return "—";

        return <PlayerCell player={{ ...player, id: player.id ?? row.original.playerId }} />;
      },
    },
    {
      id: "assignment_status",
      accessorFn: row => row.status ?? "",
      header: ctx => {
        const generateStatusItem = (label: string, status: SuperPageAssignment.Status): DropdownMenu.Item.Props => {
          var onClick: () => void;
          var icon: React.ReactNode;
          if (status === assignmentStatus) {
            onClick = () => setAssignmentStatus(undefined);
            icon = <CircleCheck className="size-4 text-primary" />;
          } else {
            onClick = () => setAssignmentStatus(status);
            icon = <Circle className="size-4 text-muted-foreground" />;
          }

          return { label, onClick, icon, closeOnClick: false };
        };
        return (
          <div className="flex min-w-0 items-center justify-center gap-1">
            <DropdownMenu
              renderTrigger={props => (
                <Button
                  type="button"
                  variant="ghost"
                  className="relative h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-foreground"
                  {...props}
                >
                  <ListFilter className="h-3.5 w-3.5" />
                  {assignmentStatus && (
                    <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
                  )}
                </Button>
              )}
              items={[
                generateStatusItem("Available", "AVAILABLE"),
                generateStatusItem("Requested", "REQUESTED"),
                generateStatusItem("Completed", "COMPLETED"),
              ]}
            />
            <div className="min-w-0 shrink">{assignmentStatusSortableHeader(ctx)}</div>
          </div>
        );
      },
      cell: ({ row }) => {
        const assignment = row.original;
        const playerName = assignment.player?.fullName ?? "This Player";

        const handleClick = async () => {
          if (assignment.status === "AVAILABLE") {
            const response = await confirmDialog({
              title: "Request Video Page",
              description: (
                <div>
                  <span className="font-semibold">{playerName}</span> already has access to this Video Page assignment.
                  Sending this request will mark it as something they need to complete.
                  <br />
                  <br />
                  Send the request now?
                </div>
              ),
              size: "md",
              confirmLabel: "Yes, send request",
            });
            if (response) {
              handleRequestAssignments([assignment]);
            }
          } else {
            setEditingAssignment(assignment);
          }
        };

        let assignmentTimestamps = [];
        if (assignment.status === "REQUESTED") {
          if (assignment.viewedAt) {
            assignmentTimestamps.push(timestampLabel(assignment.viewedAt, "Viewed"));
          }
          if (assignment.lastRemindedAt) {
            assignmentTimestamps.push(timestampLabel(assignment.lastRemindedAt, "Notified"));
          }
        }

        return (
          <div className="flex flex-col items-center">
            <Button variant="ghost" className="text-primary/80 hover:text-primary" onClick={handleClick}>
              <SuperPageAssignmentStatus status={assignment.status} viewedAt={assignment.viewedAt} includeLabel />
            </Button>
            {assignmentTimestamps.map((timestamp, index) => (
              <div className="text-xs text-muted-foreground" key={`assignment-timestamp-${index}`}>
                {timestamp}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      id: "view_count",
      accessorKey: "totalViews",
      header: createSortableHeader("Views"),
      cell: ({ row }) => row.original.totalViews,
    },
    {
      id: "click_count",
      accessorKey: "totalClicks",
      header: createSortableHeader("Clicks"),
      cell: ({ row }) => row.original.totalClicks,
    },
    {
      id: "actions",
      meta: { intrinsic: true },
      header: () => {
        const items: DropdownMenu.Item.Props[] = [];
        const availableAssignments = Array.from(selectedAssignments.values()).filter(a => a.status === "AVAILABLE");
        const requestedAssignments = Array.from(selectedAssignments.values()).filter(a => a.status === "REQUESTED");

        if (availableAssignments.length > 0) {
          items.push({
            label: pluralize("AVAILABLE ASSIGNMENT", availableAssignments.length, true),
            disabled: true,
            closeOnClick: false,
          });
          items.push({
            label: `Request ${pluralize("Assignment", availableAssignments.length)}`,
            icon: <Plus className="size-4" />,
            onClick: () => handleRequestAssignments(availableAssignments),
          });
        }
        if (requestedAssignments.length > 0) {
          items.push({
            label: pluralize("REQUESTED ASSIGNMENT", requestedAssignments.length, true),
            disabled: true,
            closeOnClick: false,
          });
          if (superPageIsActive) {
            items.push({
              label: `Send ${pluralize("Reminder", requestedAssignments.length)}`,
              icon: <BellRing className="size-4" />,
              onClick: () => remindAssignments(superPage, requestedAssignments),
            });
            items.push({
              label: `Cancel ${pluralize("Request", requestedAssignments.length)}`,
              icon: <X className="size-4" />,
              onClick: () => cancelRequestAssignments(superPage, requestedAssignments),
            });
          }
        }

        return (
          <DropdownMenu
            renderTrigger={triggerProps => (
              <Button
                {...triggerProps}
                variant={items.length === 0 ? "ghost" : "default"}
                disabled={items.length === 0}
              >
                Actions
              </Button>
            )}
            items={items}
          />
        );
      },
      cell: ({ row }) => {
        const assignment = row.original;
        const items: DropdownMenu.Item.Props[] = [
          { label: "Copy Link", onClick: () => handleCopyLink(assignment), icon: <Link className="size-4" /> },
        ];
        const completed = assignment.status === "COMPLETED";
        const available = assignment.status === "AVAILABLE";
        if (available) {
          items.push({
            label: "Request",
            onClick: () => handleRequestAssignments([assignment]),
            icon: <Plus className="size-4" />,
          });
        } else if (completed) {
          items.push({
            label: "Edit",
            onClick: () => setEditingAssignment(assignment),
            icon: <Pencil className="size-4" />,
          });
          items.push({
            label: "Preview",
            onClick: () => onPreviewAssignment(assignment),
            icon: <Eye className="size-4" />,
          });
          items.push({
            label: "Open in New Tab",
            onClick: () => handleOpenInNewTab(assignment),
            icon: <SquareArrowOutUpRight className="size-4" />,
          });
        } else {
          if (superPageIsActive) {
            const label = (
              <div className="flex flex-col">
                <span>Send Reminder</span>
                {assignment.lastRemindedAt && (
                  <span className="text-xs text-muted-foreground">
                    {timestampLabel(assignment.lastRemindedAt, "Last reminded")}
                  </span>
                )}
              </div>
            );
            items.push({
              label,
              onClick: () => handleRemindAssignment(assignment),
              icon: <BellRing className="size-4" />,
            });
          }
          items.push({
            label: "Cancel Request",
            onClick: () => handleCancelRequestAssignment(assignment),
            icon: <X className="size-4" />,
          });
        }

        return (
          <div className="flex items-center justify-center">
            <DropdownMenu
              renderTrigger={props => (
                <Button {...props} variant="ghost" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              )}
              items={items}
            />
          </div>
        );
      },
    },
  ];

  const videoPageModalInitialObject = useMemo(() => {
    if (editingAssignment) {
      return { superPageAssignmentId: editingAssignment.id };
    }
    return undefined;
  }, [editingAssignment?.id]);

  return (
    <div className="flex flex-col gap-4 w-5xl min-w-md">
      <VideoPageModal
        open={!!editingAssignment}
        initialObject={videoPageModalInitialObject}
        onClose={() => setEditingAssignment(undefined)}
      />
      <div className="flex items-center gap-4">
        <Card className="flex-1 shadow-md">
          <Card.Content>
            <div className="flex gap-1 items-center justify-between">
              <div className="flex flex-col gap-2">
                <div className="text-xl font-bold indent-px">Total Views</div>
                <div className="text-4xl font-bold indent-px">{superPage.totalViews ?? 0}</div>
              </div>
              <div className="bg-accent p-2 rounded-full">
                <Eye className="size-10" />
              </div>
            </div>
          </Card.Content>
        </Card>
        <Card className="flex-1 shadow-md">
          <Card.Content>
            <div className="flex gap-1 items-center justify-between">
              <div className="flex flex-col gap-2">
                <div className="text-xl font-bold indent-px">Total Clicks</div>
                <div className="text-4xl font-bold indent-px">{superPage.totalClicks ?? 0}</div>
              </div>
              <div className="bg-accent p-2 rounded-full">
                <MousePointerClick className="size-10" />
              </div>
            </div>
          </Card.Content>
        </Card>
        <Card className="flex-1 shadow-md">
          <Card.Content>
            <div className="flex flex-col gap-2">
              <div className="text-xl font-bold indent-px">Completion Rate</div>
              <Progress value={completePercentage} className="w-full max-w-sm">
                <Progress.Label className="text-lg font-semibold">
                  {totalCompleted}/{totalActionable} Completed ({completePercentage}%)
                </Progress.Label>
              </Progress>
            </div>
          </Card.Content>
        </Card>
      </div>

      <div className="flex flex-col gap-4">
        {!superPageIsActive && (
          <Alert variant="warning">
            <AlertTriangleIcon />
            <Alert.Title className="font-semibold">Super Page not yet published</Alert.Title>
            <Alert.Description className="block">
              This Super Page is currently hidden from players. To make it visible, publish it in the{" "}
              <Button variant="link" onClick={onGoToPageDetails} className="p-0 h-auto">
                Page Details tab
              </Button>
              .
            </Alert.Description>
          </Alert>
        )}
        <div className="flex gap-4 items-center justify-between mt-4">
          <Input placeholder="Search Player..." value={search} onChange={e => setSearch(e.target.value)} />
          {withWritePermission && <Button children="Edit Eligibility" onClick={() => setShowEligibility(true)} />}
        </div>
        <SuperPageEligibilityDialog
          open={showEligibility}
          superPageId={superPage.id}
          onOpenChange={setShowEligibility}
        />
        <DataTable
          columns={columns}
          data={assignments}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          disabled={isFetching}
          emptyState={
            <div className="flex flex-col items-center gap-3 py-2">
              {isFetching ? (
                <Spinner className="size-5" />
              ) : (
                <>
                  <div>
                    {hasFiltersApplied
                      ? "No Players match your current filters."
                      : "No Players are eligible for this Super Page."}
                  </div>
                  {!hasFiltersApplied && withWritePermission && (
                    <Button size="sm" onClick={() => setShowEligibility(true)}>
                      Edit Eligibility
                    </Button>
                  )}
                </>
              )}
            </div>
          }
          pageCount={totalPages}
          rowCount={totalElements}
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
        />
      </div>
    </div>
  );
};

export { SuperPagePlayers };
